import { Component, computed, inject, input, signal } from '@angular/core';
import { BootstrapDialogService, UtilityService } from '@eforall/common';
import { AddressLink, CallLink, EmailLink, Link, SmsLink, ToolLink, WebLink } from '../../types/link';
import { LongPressDirective } from './long-press.directive';
import { IconStyle } from '../../types/icon-style';

interface IconInfo {
	icon: string;
	style: IconStyle;
	text: string;
}

@Component({
	selector: 'link-button',
	standalone: true,
	imports: [LongPressDirective],
	templateUrl: './link-button.widget.html',
	styleUrls: ['./link-button.widget.scss']
})
export class LinkButtonWidget {
	public readonly link = input.required<Link>();
	public readonly iconStyle = input<IconStyle>('fa-regular');

	private util = inject(UtilityService);
	private dialogService = inject(BootstrapDialogService);

	private longPress = signal(false);
	private iconInfo = computed(() => this.getIconInfo());
	private clipboardIconInfo = computed(() => this.getClipboardIcon());
	private clipboardText = computed(() => this.getClipboardText());
	private urlValue = computed(() => this.getUrl());
	private targetValue = computed(() => this.getTarget());
	private tooltipValue = computed(() => this.getTooltip());

	public iconClass = computed(() => {
		const icon = this.longPress() ? this.clipboardIconInfo() : this.iconInfo();
		return `fa-fw ${icon.style} ${icon.icon}`;
	});

	public iconText = computed(() => {
		const icon = this.longPress() ? this.clipboardIconInfo() : this.iconInfo();
		return icon.text;
	});

	public href = computed(() => {
		return this.longPress() ? '#' : this.urlValue();
	});

	public target = computed(() => this.targetValue());
	public tooltip = computed(() => this.tooltipValue());


	onLongPress(longPress: boolean) {
		this.longPress.set(longPress);
	}

	public async click(event: MouseEvent): Promise<boolean | void> {
		if (this.longPress()) {
			event.preventDefault();

			const text = this.clipboardText();
			await this.util.clipboard.writeText(text);
			await this.dialogService.showMessage(`The following text has been copied to the clipboard.\n\n${text}`);

			this.longPress.set(false);
			return false;
		}
	}

	private getClipboardIcon(): IconInfo {
		return {
			icon: 'fa-clipboard',
			style: 'fa-regular',
			text: this.iconInfo().text
		};
	}

	private getClipboardText(): string {
		const link = this.link();
		switch (link.linkType) {
			case 'address':
				return this.getAddressText(link);
			case 'call':
				return this.util.phone.format(link.phone, link.ext);
			case 'email':
				return link.email;
			case 'sms':
				return link.phone;
			case 'webLink':
				// Copy the text displayed to user, not the URL
				return this.getIconInfo().text;
			case 'tool':
				// Copy the text displayed to user, not the URL
				return this.getIconInfo().text;
			default:
				return '';
		}
	}

	private getIconInfo(): IconInfo {
		const link = this.link();
		switch (link.linkType) {
			case 'address':
				return this.configureAddressIcon(link);
			case 'call':
				return this.configureCallIcon(link);
			case 'email':
				return this.configureEmailIcon(link);
			case 'sms':
				return this.configureSmsIcon(link);
			case 'webLink':
				return this.configureWebLinkIcon(link);
			case 'tool':
				return this.configureToolLinkIcon(link);
		}
	}

	private getUrl(): string {
		const link = this.link();
		switch (link.linkType) {
			case 'address':
				return this.buildMapLink(link);
			case 'call':
				return this.util.phone.makePhoneUrl(link.phone, 'tel');
			case 'email':
				return `mailto:${link.email}`;
			case 'sms':
				return this.util.phone.makePhoneUrl(link.phone, 'sms');
			case 'webLink':
				return link.url;
			case 'tool':
				return link.url;
		}
	}

	private getTarget(): string {
		const link = this.link();
		switch (link.linkType) {
			case 'address':
				return 'google-map';
			case 'call':
			case 'email':
			case 'sms':
				return '_self';
			case 'webLink':
				// Target should be the text shown (Website, Facebook, Twitter, etc)
				return this.getIconInfo().text;
			case 'tool':
				// Target should be the text shown (Tool Enabled, Tool Disabled)
				return this.getIconInfo().text;
		}
	}

	private getTooltip(): string {
		const link = this.link();
		switch (link.linkType) {
			case 'address':
				// Show the address in tooltip, or fallback to 'Google Map' if no address
				const addressText = this.getAddressText(link);
				return addressText !== 'Address' ? `Open in Google Maps: ${addressText}` : 'Google Map';
			case 'call':
				return `Call ${this.util.phone.format(link.phone, link.ext)}`;
			case 'email':
				return `Email ${link.email}`;
			case 'sms':
				return `Text ${this.util.phone.format(link.phone)}`;
			case 'webLink':
				return this.getWebLinkTooltip(link.url);
			case 'tool':
				return this.getWebLinkTooltip(link.url);
		}
	}

	private getWebLinkTooltip(url: string): string {
		const urlSplit = url.split('://');
		let tooltip = urlSplit[urlSplit.length - 1] || '';
		if (tooltip) {
			if (tooltip.endsWith('/')) tooltip = tooltip.substring(0, tooltip.length - 1);
			if (tooltip.toLowerCase().startsWith('www.')) tooltip = tooltip.substring(4);
		}
		return tooltip;
	}

	private configureAddressIcon(link: AddressLink): IconInfo {
		// Note: In a real implementation, you'd need to get zip code info from DataService
		// For now, using a simplified version
		const text = link.street || 'Address';
		return {
			icon: 'fa-map-location-dot',
			style: this.iconStyle(),
			text
		};
	}

	private configureCallIcon(link: CallLink): IconInfo {
		const text = this.util.phone.format(link.phone, link.ext);
		return {
			icon: 'fa-phone',
			style: this.iconStyle(),
			text
		};
	}

	private configureEmailIcon(link: EmailLink): IconInfo {
		return {
			icon: 'fa-envelope',
			style: this.iconStyle(),
			text: link.email
		};
	}

	private configureSmsIcon(link: SmsLink): IconInfo {
		return {
			icon: 'fa-comment-alt-lines',
			style: this.iconStyle(),
			text: 'Text (SMS)'
		};
	}

	private configureWebLinkIcon(link: WebLink): IconInfo {
		// Simplified - in real implementation you'd map webLinkTypeId to appropriate icons
		return {
			icon: 'fa-link',
			style: this.iconStyle(),
			text: link.overrideText || 'Website'
		};
	}

	private configureToolLinkIcon(link: ToolLink): IconInfo {
		return {
			icon: link.enabled ? 'fa-wrench' : 'fa-wrench',
			style: this.iconStyle(),
			text: link.overrideText || (link.enabled ? 'Tool Enabled' : 'Tool Disabled')
		};
	}

	private getAddressText(link: AddressLink): string {
		// Simplified - in real implementation you'd get zip code from domain data
		return link.street || 'Address';
	}

	private buildMapLink(link: AddressLink): string {
		const parts: string[] = [];

		const street = (link.street || '').trim();
		if (street.length) {
			parts.push(street.split(' ').filter(p => !!p.length).join('+'));
		}

		// Note: In a real implementation, you'd need to get zip code info from domain data
		// For now, creating a simplified version
		return `https://www.google.com/maps?q=` + parts.join(',');
	}
}
